import { useEffect, useRef, useState } from 'react';
import { Loader2, MessageSquare, Send } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Separator } from '../../components/ui/separator';
import { usePostMessage, useRequestThread } from '../../hooks/useSupportRequests';
import type { RequestMessage } from '../../services/supportRequest.service';
import { MessageRow } from './MessageRow';

/**
 * One thread: the root message, then its replies, then a composer.
 *
 * Used in two places with no difference in behaviour — the right-hand panel of
 * the artifact takeover, and the thread drawer in the conversation. Both take
 * any message id and let the backend resolve the root, which is what makes the
 * two cases in the spec (artifact posted top-level vs. posted inside a thread)
 * collapse into a single component.
 */
export function ThreadPanel({
  requestId,
  messageId,
  /** Rendered above the root message — the takeover uses it to show which
   *  artifact the reader is looking at. */
  contextCard,
  emptyHint = 'No replies yet. Start the thread.',
  onOpenArtifact,
  openArtifactId,
  /** Rendered at the right of the panel header. The takeover puts its close
   *  button here so it sits at the top right of the modal as a whole, rather
   *  than over the document pane. */
  headerAction,
}: {
  requestId: string;
  messageId: string;
  contextCard?: React.ReactNode;
  emptyHint?: string;
  onOpenArtifact?: (artifactId: string, messageId: string) => void;
  /** Set by the takeover so the artifact being read loses its "View full". */
  openArtifactId?: string | null;
  headerAction?: React.ReactNode;
}) {
  const { data: thread, isLoading } = useRequestThread(requestId, messageId);
  const postMessage = usePostMessage(requestId);
  const [body, setBody] = useState('');
  const bottomRef = useRef<HTMLDivElement>(null);

  const replies: RequestMessage[] = thread?.replies ?? [];

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [replies.length]);

  const send = async () => {
    const text = body.trim();
    if (!text || !thread) return;
    await postMessage.mutateAsync({
      body: text,
      // Always the root — replies never nest, matching the backend rule.
      parentMessageId: thread.root.id,
    });
    setBody('');
  };

  const header = (
    <div className="px-4 py-3 border-b border-border flex-shrink-0 flex items-center justify-between gap-2">
      <h2 className="text-sm font-medium text-foreground flex items-center gap-2 min-w-0">
        <MessageSquare className="h-4 w-4 flex-shrink-0" />
        Thread
        {replies.length > 0 && (
          <span className="text-muted-foreground font-normal">
            ({replies.length} {replies.length === 1 ? 'reply' : 'replies'})
          </span>
        )}
      </h2>
      {headerAction}
    </div>
  );

  // The header renders in both branches on purpose: it carries the takeover's
  // close button, and the takeover suppresses Escape, so a header that only
  // appeared once the thread loaded would leave a modal with no way out.
  if (isLoading || !thread) {
    return (
      <div className="flex flex-col h-full min-h-0">
        {header}
        <div className="flex-1 flex items-center justify-center text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin mr-2" />
          Loading thread
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full min-h-0">
      {header}

      <div className="flex-1 overflow-y-auto min-h-0 px-4 py-3 space-y-4">
        {contextCard}

        {/* The message the thread hangs off, always shown first — Slack's
            "original message" header. */}
        <MessageRow
          message={thread.root}
          requestId={requestId}
          compact
          onOpenArtifact={onOpenArtifact}
          openArtifactId={openArtifactId}
        />

        <div className="flex items-center gap-2">
          <Separator className="flex-1" />
          <span className="text-[11px] text-muted-foreground">
            {replies.length > 0
              ? `${replies.length} ${replies.length === 1 ? 'reply' : 'replies'}`
              : 'No replies'}
          </span>
          <Separator className="flex-1" />
        </div>

        {replies.length === 0 ? (
          <p className="text-xs text-muted-foreground text-center py-2">{emptyHint}</p>
        ) : (
          replies.map((m) => (
            <MessageRow
              key={m.id}
              message={m}
              requestId={requestId}
              compact
              onOpenArtifact={onOpenArtifact}
              openArtifactId={openArtifactId}
            />
          ))
        )}
        <div ref={bottomRef} />
      </div>

      <div className="border-t border-border p-3 flex-shrink-0">
        <div className="flex items-end gap-2">
          <Textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            placeholder="Reply in thread"
            rows={2}
            className="resize-none min-h-0 text-sm"
          />
          <Button
            onClick={send}
            disabled={!body.trim() || postMessage.isPending}
            size="icon"
            className="h-9 w-9 flex-shrink-0"
            aria-label="Send reply"
          >
            {postMessage.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
